import { isServer, parsedUrl } from '@verifime/utils';
import type { AuthMethod } from '@/utils/authMethodAnalytics';
import { readDeclaredEntityType, type TInviteOnboardAsMode } from '@/utils/onboardAsMode';

const DISMISSED_RECOVERY_METHOD_ALERT = 'dismissedRecoveryMethodAlert';
const LAST_DISMISSED_PERMISSION_REQUEST_ID = 'lastDismissedPermissionRequestId';
const ASSOCIATED_PARTY_ASSIST_OPT_OUT_FLAGGED = 'associatedPartyAssistOptOutFlagged';
const TRACK_ID_ACTION_NEXT_PAGE = 'trackIdActionNextPage';

/**
 * The same unspent magic link this page was loaded with, in /token/<jwt> path form, derived from
 * the URL fragment the token bridge (pages/token/[tokenValue].tsx) leaves behind after its own
 * path-to-fragment redirect. Needed only by the desktop QR reveal: a fragment is client-only
 * (RFC 3986 section 3.5) and not reliably preserved by a QR scanner, an email client, or an HTTP
 * redirect, which is exactly why the token bridge exists to convert a delivered path-form URL
 * into this page's own fragment form in the first place.
 *
 * Deliberately the literal inverse of transformTokenUrl: if that function's encoding ever
 * changes, this one must change with it, which is exactly what utils.test.ts's round-trip test
 * guards.
 */
export function getMagicLinkQrUrl(): string | undefined {
  if (isServer) {
    return undefined;
  }
  const { hash, urlParams } = parsedUrl();
  const token = hash.slice(1); // location.hash includes the leading '#'
  if (!token) {
    return undefined;
  }
  // Both directions this inverts preserve query parameters explicitly (transformTokenUrl copies
  // them onto the fragment URL; the backend's replaceFirstHashWithToken does the same on the way
  // back), so this must too, or a magic link minted with one silently loses it in the QR code.
  const query = urlParams.toString();
  return `${location.origin}/token/${token}${query ? `?${query}` : ''}`;
}

export function getTrackIdFromUrl() {
  const { urlParams } = parsedUrl();
  return urlParams.get('trackId');
}

export function getActionFromUrl() {
  if (isServer) {
    return;
  }
  const { urlParams } = parsedUrl();
  return urlParams.get('action');
}

const LAST_AUTH_EMAIL_URL_KEY = 'lastAuthEmail';

export function getLastAuthEmailFromUrl() {
  if (isServer) {
    return;
  }
  const { urlParams } = parsedUrl();
  return urlParams.get(LAST_AUTH_EMAIL_URL_KEY);
}

export function clearLastAuthEmailFromUrl() {
  const url = new URL(location.href);
  url.searchParams.delete(LAST_AUTH_EMAIL_URL_KEY);
  history.replaceState(null, '', url.href);
}

export function getSignUpCodeFromUrl() {
  const { urlParams } = parsedUrl();
  return urlParams.get('signUpCode');
}

const TRACK_ID_KEY = 'TRACK_ID';

export function saveTrackIdToStorage(trackId: string) {
  sessionStorage.setItem(TRACK_ID_KEY, trackId);
  // A new invite starts afresh: an earlier invite's journey client must not take its grant.
  clearJourneyOrganisationFromStorage();
}

export function clearTrackIdFromStorage() {
  sessionStorage.removeItem(TRACK_ID_KEY);
}

export function getTrackIdFromStorage() {
  if (isServer) {
    return null;
  }
  return sessionStorage.getItem(TRACK_ID_KEY);
}

/**
 * The activation's answer (`onboardAsMode`) for each invite this tab activated, keyed by tracking
 * reference, with the invite's client (Entity Selection First, VME-4142). Read by the onboard-as mode rules
 * in `onboardAsMode.ts` for the current invite only.
 */
const INVITE_ONBOARD_AS_MODE_KEY = 'INVITE_ONBOARD_AS_MODE';

type TInviteOnboardAsModeEntry = Omit<TInviteOnboardAsMode, 'trackingReference'>;

function readInviteOnboardAsModes(): Record<string, TInviteOnboardAsModeEntry> {
  if (isServer) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(INVITE_ONBOARD_AS_MODE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, TInviteOnboardAsModeEntry>)
      : {};
  } catch {
    return {};
  }
}

export function saveInviteOnboardAsModeToStorage({
  trackingReference,
  ...entry
}: TInviteOnboardAsMode) {
  const onboardAsModes = readInviteOnboardAsModes();
  onboardAsModes[trackingReference] = entry;
  sessionStorage.setItem(INVITE_ONBOARD_AS_MODE_KEY, JSON.stringify(onboardAsModes));
  // An activation (or a refused confirmation) starts the invite afresh, as saving it does.
  clearJourneyOrganisationFromStorage();
}

/** The stored answer for one invite; undefined when there is none or it is not a valid answer. */
export function getInviteOnboardAsModeFromStorage(
  trackingReference: string | null | undefined,
): TInviteOnboardAsMode | undefined {
  if (!trackingReference) {
    return undefined;
  }
  const entry: unknown = readInviteOnboardAsModes()[trackingReference];
  if (!entry || typeof entry !== 'object') {
    return undefined;
  }
  const { organisationId, onboardAsMode, answeredAt, declaredEntityType } =
    entry as Partial<TInviteOnboardAsModeEntry>;
  if (onboardAsMode !== 'AskFirst' && onboardAsMode !== 'Legacy') {
    return undefined;
  }
  return {
    trackingReference,
    organisationId: typeof organisationId === 'string' ? organisationId : undefined,
    onboardAsMode,
    declaredEntityType: readDeclaredEntityType(declaredEntityType),
    answeredAt: typeof answeredAt === 'string' ? answeredAt : undefined,
  };
}

/** The stored answer for the invite whose tracking reference is in session storage. */
export function getCurrentInviteOnboardAsModeFromStorage(): TInviteOnboardAsMode | undefined {
  return getInviteOnboardAsModeFromStorage(getTrackIdFromStorage());
}

/**
 * The client of the ask-first journey the customer last continued from the confirm screen
 * (VME-4142). The consents step grants to it, including for a journey resumed with no invite in
 * this session.
 */
const JOURNEY_ORGANISATION_KEY = 'JOURNEY_ORGANISATION';

export function saveJourneyOrganisationToStorage(organisationId: string) {
  sessionStorage.setItem(JOURNEY_ORGANISATION_KEY, organisationId);
}

/**
 * Resuming a client by organisation (Home's entry, not an invite) makes it the journey: the
 * session's invite (tracking reference and its activation answers) is cleared, as main clears the
 * tracking reference when an identity is added, so an earlier invite for another client cannot
 * take this journey's grant. Reopening an invite link stores it again on activation.
 */
export function continueJourneyByClient(organisationId: string) {
  clearTrackIdFromStorage();
  sessionStorage.removeItem(INVITE_ONBOARD_AS_MODE_KEY);
  // A new journey: the confirm screen keeps its own chosen identity after this, when it has one.
  clearJourneyChosenIdentityFromStorage();
  saveJourneyOrganisationToStorage(organisationId);
}

/** Sign-out: the tracking reference (as main), the activation answers and the journey client. */
export function clearSessionInviteFromStorage() {
  clearTrackIdFromStorage();
  sessionStorage.removeItem(INVITE_ONBOARD_AS_MODE_KEY);
  clearJourneyOrganisationFromStorage();
}

/** Every journey reset that clears the journey client clears the identity chosen for it too. */
export function clearJourneyOrganisationFromStorage() {
  sessionStorage.removeItem(JOURNEY_ORGANISATION_KEY);
  clearJourneyChosenIdentityFromStorage();
}

export function getJourneyOrganisationFromStorage(): string | null {
  if (isServer) {
    return null;
  }
  return sessionStorage.getItem(JOURNEY_ORGANISATION_KEY);
}

/**
 * The wallet identity the customer chose on the confirm screen, kept with the client it was chosen
 * for (Greengate-Fintech/verifime-adr#731). The Individual's consents step reads it to hand the
 * chosen entity on. It is read only for that client, and only after the wallet confirms it.
 */
export const JOURNEY_CHOSEN_IDENTITY_KEY = 'JOURNEY_CHOSEN_IDENTITY';

export type TJourneyChosenIdentity = { organisationId: string; identityId: string };

export function saveJourneyChosenIdentityToStorage(chosen: TJourneyChosenIdentity) {
  const { organisationId, identityId } = chosen;
  sessionStorage.setItem(
    JOURNEY_CHOSEN_IDENTITY_KEY,
    JSON.stringify({ organisationId, identityId }),
  );
}

export function clearJourneyChosenIdentityFromStorage() {
  sessionStorage.removeItem(JOURNEY_CHOSEN_IDENTITY_KEY);
}

/** The kept choice; null when there is none or what is stored is not a client and an identity. */
export function getJourneyChosenIdentityFromStorage(): TJourneyChosenIdentity | null {
  if (isServer) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(
      sessionStorage.getItem(JOURNEY_CHOSEN_IDENTITY_KEY) ?? 'null',
    );
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const { organisationId, identityId } = parsed as Record<string, unknown>;
    return typeof organisationId === 'string' &&
      organisationId &&
      typeof identityId === 'string' &&
      identityId
      ? { organisationId, identityId }
      : null;
  } catch {
    return null;
  }
}

const SIGN_UP_CODE_KEY = 'SIGN_UP_CODE';

export function saveSignUpCodeToStorage(code: string) {
  sessionStorage.setItem(SIGN_UP_CODE_KEY, code);
}

export function clearSignUpCodeFromStorage() {
  sessionStorage.removeItem(SIGN_UP_CODE_KEY);
}

export function getSignUpCodeFromStorage() {
  if (isServer) {
    return null;
  }
  return sessionStorage.getItem(SIGN_UP_CODE_KEY);
}

export function getDismissedRecoveryMethodAlert() {
  if (isServer) {
    return null;
  }
  return sessionStorage.getItem(DISMISSED_RECOVERY_METHOD_ALERT);
}

export function setDismissedRecoveryMethodAlertToTrue() {
  sessionStorage.setItem(DISMISSED_RECOVERY_METHOD_ALERT, 'true');
}

export function getLastDismissedPermissionRequestId() {
  if (isServer) {
    return null;
  }
  return sessionStorage.getItem(LAST_DISMISSED_PERMISSION_REQUEST_ID);
}

export function setLastDismissedPermissionRequestId(permissionRequestId: string) {
  sessionStorage.setItem(LAST_DISMISSED_PERMISSION_REQUEST_ID, permissionRequestId);
}

function getAssociatedPartyAssistOptOutFlag(): string[] {
  if (isServer) {
    return [];
  }
  const asJsonStr = sessionStorage.getItem(ASSOCIATED_PARTY_ASSIST_OPT_OUT_FLAGGED);
  return asJsonStr ? JSON.parse(asJsonStr) : [];
}

export function hasOptedOutAssoicatedPartyAssist(customerId: string) {
  const associatedPartyAssistOptOutFlagged = getAssociatedPartyAssistOptOutFlag();
  return associatedPartyAssistOptOutFlagged.includes(customerId);
}

export function optOutAssociatedPartyAssist(customerIdToBeFlagged: string) {
  if (!isServer && !hasOptedOutAssoicatedPartyAssist(customerIdToBeFlagged)) {
    const associatedPartyAssistOptOutFlagged = getAssociatedPartyAssistOptOutFlag();
    associatedPartyAssistOptOutFlagged.push(customerIdToBeFlagged);
    sessionStorage.setItem(
      ASSOCIATED_PARTY_ASSIST_OPT_OUT_FLAGGED,
      JSON.stringify(associatedPartyAssistOptOutFlagged),
    );
  }
}

export function optInAssociatedPartySetupAssist(customerIdToBeRemoved: string) {
  if (!isServer && hasOptedOutAssoicatedPartyAssist(customerIdToBeRemoved)) {
    const associatedPartyAssistOptOutFlagged = getAssociatedPartyAssistOptOutFlag();
    const updatedAssociatedPartyAssistOptOutFlagged = associatedPartyAssistOptOutFlagged.filter(
      (customerId) => customerId !== customerIdToBeRemoved,
    );
    sessionStorage.setItem(
      ASSOCIATED_PARTY_ASSIST_OPT_OUT_FLAGGED,
      JSON.stringify(updatedAssociatedPartyAssistOptOutFlagged),
    );
  }
}

export function setTrackIdActionNextPage(nextPage: string) {
  sessionStorage.setItem(TRACK_ID_ACTION_NEXT_PAGE, nextPage);
}

export function getTrackIdActionNextPage() {
  if (isServer) {
    return null;
  }
  return sessionStorage.getItem(TRACK_ID_ACTION_NEXT_PAGE);
}

export function clearTrackIdActionNextPage() {
  sessionStorage.removeItem(TRACK_ID_ACTION_NEXT_PAGE);
}

const AUTH_METHOD_KEY = 'AUTH_METHOD';

export function saveAuthMethodToStorage(method: AuthMethod) {
  sessionStorage.setItem(AUTH_METHOD_KEY, method);
}

export function clearAuthMethodFromStorage() {
  sessionStorage.removeItem(AUTH_METHOD_KEY);
}

export function getAuthMethodFromStorage(): AuthMethod | null {
  if (isServer) {
    return null;
  }
  const value = sessionStorage.getItem(AUTH_METHOD_KEY);
  if (value === 'GOOGLE' || value === 'MAGIC_LINK') {
    return value;
  }
  return null;
}
